import Link from "next/link";
import { GetStaticProps, InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";

import { AppShell } from "@chef/feature-app-shell";
import { Button } from "@chef/components";
import {
  ContentComponent,
  ISection,
  SanityQuery,
  getSectionData,
  useSectionData,
} from "@chef/feature-sanity";
import { BRAND_PATHS } from "@chef/constants";
import { Success } from "@chef/icons/symbol";
import {
  useGetDirectPaymentSessionQuery,
  useValidateDirectOrderQuery,
} from "@chef/state-management";
import { PageLoader } from "@chef/smart-components";

import { GiftCardErrorState } from "../../components/GiftCardErrorState";

import { intl } from "./GiftCardSuccessPage.Intl";

export const GiftCardSuccessPage = ({
  giftCardSuccessContent,
}: InferGetStaticPropsType<typeof GiftCardSuccessPageStaticProps>) => {
  const router = useRouter();

  const token = router.query["t"] as string;

  const { data: directPaymentSession } = useGetDirectPaymentSessionQuery(
    { token },
    { skip: !token },
  );

  const id = directPaymentSession?.directOrderId;

  const { isError, isLoading, isUninitialized } = useValidateDirectOrderQuery(
    id!,
    {
      skip: !id,
    },
  );

  const [giftCardSuccessFooter] = useSectionData(giftCardSuccessContent);

  if (!token) {
    return <PageLoader wholeScreen />;
  }

  if (isError) {
    return <GiftCardErrorState />;
  }

  if (isUninitialized || isLoading) {
    return <PageLoader wholeScreen />;
  }

  return (
    <AppShell title={intl.GIFT_CARD}>
      <AppShell.Meta />

      <AppShell.Header />

      <AppShell.Main grid>
        <div
          className="flex flex-col col-span-4 p-6 px-8 mx-auto mb-2 md:px-0 xl:col-span-6 xl:col-start-4 md:col-span-4 md:col-start-3"
          id="giftcard-success-page"
        >
          <div className="flex justify-center mx-auto">
            <Success className="w-20 h-20" />
          </div>
          <h1 className="text-center mt-11">
            <strong>{intl.GIFTCARD_PURCHASED}</strong>
          </h1>
          <p className="mt-4 text-center">
            {intl.WE_HAVE_SENT_YOU_AN_EMAIL}{" "}
            <strong>{directPaymentSession?.subject?.email}</strong>.{" "}
            {intl.YOU_CAN_FORWARD_IT_DIRECTLY_OR_PRINT}
          </p>
          <div className="text-center my-14">
            <Link href={BRAND_PATHS.GIFTCARD_PAGE} passHref>
              <Button primary>{intl.BUY_A_NEW_GIFTCARD}</Button>
            </Link>
          </div>
          <hr className="text-grey-2" />

          {giftCardSuccessFooter && (
            <ContentComponent block={giftCardSuccessFooter.content} />
          )}
        </div>
      </AppShell.Main>

      <AppShell.Footer />
      <AppShell.TabBar />
    </AppShell>
  );
};

export const GiftCardSuccessPageStaticProps: GetStaticProps<
  {
    draftMode: boolean;
    giftCardSuccessContent: ISection;
  },
  SanityQuery
> = async ({ draftMode = false }) => {
  const giftCardSuccessContent = await getSectionData("giftcard_success", {
    preview: draftMode,
  });

  return {
    props: {
      draftMode,
      giftCardSuccessContent: giftCardSuccessContent || null,
    },
    revalidate: 600,
  };
};
